import { Link } from "~/components/Link";
import { ArrowUpRight } from "lucide-react";
import { cn } from "~/lib/utils";
import { CatalogUserEvents } from "~/modules/analytics/events/catalog";
import { CatalogIcon } from "../CatalogIcon";

// // // //

export interface CatalogListItemBadge {
    title: string;
    label: string;
    mono?: boolean;
}

export interface CatalogListItemProps {
    label: string;
    slug: string;
    href: string;
    description?: string | null;
    icon: string;
    color: string;
    badges?: CatalogListItemBadge[];
}

export function CatalogListItem({
    label,
    slug,
    href,
    description,
    icon,
    color,
    badges = [],
}: CatalogListItemProps) {
    return (
        <Link
            href={href}
            analyticsLabel={CatalogUserEvents.ClickCatalogListItem}
            analyticsEventProperties={{ slug }}
            className={cn(
                "group flex flex-col",
                "rounded-xl border bg-card overflow-hidden",
                "transition-all duration-200",
                "hover:bg-background-hover",
            )}
        >
            <div className="flex items-start gap-3 px-4 pt-3 pb-3">
                <CatalogIcon icon={icon} color={color} size="md" />
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2.5">
                        <h3 className="text-sm font-semibold text-foreground leading-snug truncate">
                            {label}
                        </h3>
                        <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    </div>
                    {description && (
                        <p className="text-xs text-slate-500 leading-relaxed line-clamp-1 mt-0.5">
                            {description}
                        </p>
                    )}
                </div>
            </div>
            {badges.length > 0 && (
                <div className="flex items-center gap-6 border-t px-4 py-2.5">
                    {badges.map((badge, i) => (
                        <div key={i} className="flex flex-col min-w-0">
                            <span className="text-[0.625rem] text-muted-foreground uppercase tracking-wide mb-0.5">
                                {badge.title}
                            </span>
                            <span
                                className={cn(
                                    "text-xs text-slate-600 leading-tight line-clamp-1",
                                    badge.mono && "font-mono",
                                )}
                            >
                                {badge.label}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </Link>
    );
}
